import { HyperlinkSourceTypeDto, HyperlinkSourceGetDto } from "../types";

/**
 * How to implement additional source type.
 *
 * 1. Update DTO definition in services\hyperlink\types.ts
 * 2. Create a new file in the objecttypes folder with the name of the source type.
 * 3. Add case to the getSourceIdentifier function to return the identifier for the source type.
 * 4. Add type names to language file:
 *     - hyperlink.objectTypeDescription.{sourceType}
 *     - hyperlink.entityName.{sourceType}
 *
 */

/**
 * Function to get names for source objects
 */
type GetNamesFunction = (ids: (string | number)[]) => Map<number | string, string>;

/**
 * Gets names for source objects
 * @param type source type
 * @param links set of links to get source entiry names for
 * @returns Map of hyperlinkId to source object name
 */
export async function getSourceObjectNamesAsync<ST extends HyperlinkSourceTypeDto>(
    type: ST,
    links: { source: HyperlinkSourceGetDto | null; hyperlinkId: string | null }[]
): Promise<Map<string, string>> {
    const identifiers = links.map(link => getSourceIdentifier(link));
    let getNamesAsync: GetNamesFunction;
    let names: Map<string | number, string>;

    try {
        //import implementation of getNames function for the type
        const loader = await ((): Promise<{
            getNamesAsync: GetNamesFunction;
        }> =>
            import(
                /* webpackInclude: /(?<!\.d)\.ts$/ */
                /* webpackChunkName: "hyperlink-objecttypes" */
                /* webpackMode: "lazy-once" */
                `@/services/hyperlink/objecttypes/${type}`
            ))();
        getNamesAsync = loader.getNamesAsync;
    } catch (error) {
        console.log(`hyperlink/getNamesAsync not implemented for source type ${type}.`);
        //fallback to empty implementation
        getNamesAsync = () => {
            return new Map();
        };
    }
    //execute the getNames function
    names = await getNamesAsync(identifiers);
    //map results to hyperlinkId
    return new Map(links.map(link => [link.hyperlinkId, names.get(getSourceIdentifier(link))]));
}

export function getSourceIdentifier({ source, hyperlinkId }: { source: HyperlinkSourceGetDto | null; hyperlinkId: string | null }): string | number {
    switch (source.type) {
        case "document":
            return source.document_id;
        case "case":
            return source.case_id;
        case "control":
            return source.control_id;
        case "risk":
            return source.risk_id;
        case "object":
            return source.object_id;
        case "news_article":
            return source.news_article_id;
        case "audit":
            return source.audit_id;
        case "content_item":
            return source.content_item_id;
        case "question_list":
            return source.question_list_id;
        case "favorite":
            return source.favorite_user_id;
        case "report":
            return source.report_id;
        default:
            //unknown source, use the hyperlinkId so each link is seen as individual source item
            return hyperlinkId;
    }
}
